// Studio: Medien direkt im Baustein hochladen (Bild, Video, Bilder-Slideshow). Gleicher Weg wie Mediathek/Playlist
// (uploadFile: Chunks, Wiederaufnahme, Typprüfung und Kontingent beim Server). Nach dem Hochladen „In Prüfung …"
// (Virenscan, Verarbeitung; die Medienliste fragt dabei alle 3 s nach), sobald bereit wird das Medium im Baustein gewählt.
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useId, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, uploadFile } from '../../api/client';
import { qk } from '../../api/queries';
import type { Media } from '../../api/types';
import { Icon } from '../../components/Icon';
import { MediaView } from '../../components/Preview';
import { errorText } from '../../components/toast';
import { formatBytes } from '../../components/ui';
import { localeOf } from '../../i18n';

export type UploadKind = 'image' | 'video';
/** Vom Server erlaubte Typen (wie in der Mediathek), getrennt nach Medienart des Bausteins. */
export const ACCEPT: Record<UploadKind, string[]> = {
  image: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
  video: ['video/mp4', 'video/webm'],
};
const EXT: Record<UploadKind, RegExp> = { image: /\.(jpe?g|png|webp|gif)$/i, video: /\.(mp4|webm)$/i };
/** Passt die Datei zur Medienart? (Endgültige Prüfung über Magic Bytes macht der Server.) */
export const fitsKind = (f: Pick<File, 'name' | 'type'>, kind: UploadKind) => (f.type ? ACCEPT[kind].includes(f.type) : EXT[kind].test(f.name));

export interface StudioUploadRow {
  key: number; elId: string; field: 'asset' | 'assets'; kind: UploadKind; name: string; sent: number; total: number;
  phase: 'upload' | 'check' | 'done' | 'error'; assetId?: string; error?: string;
}
type T = (k: string, o?: Record<string, unknown>) => string;
let seq = 0;

/** Verständliche Fehlermeldung für den Upload (zu gross, Speicher voll, falscher Typ). */
export function uploadErrorText(e: unknown, t: T, locale = 'de-CH'): string {
  if (e instanceof ApiError) {
    if (e.code === 'quota_exceeded' && e.status === 422) {
      const max = Number(/(\d+)/.exec(String(e.problem.errors?.sizeBytes?.[0] ?? ''))?.[1] ?? 0);
      return max ? t('studioUpload.tooLargeMax', { max: formatBytes(max, locale) }) : t('studioUpload.tooLarge');
    }
    if (e.code === 'quota_exceeded') return t('studioUpload.storageFull');
    if (e.code === 'media_rejected') return t('studioUpload.badType');
  }
  return errorText(e, t);
}

/** Grund der Ablehnung nach der Prüfung (Virenscan, Verarbeitung). */
export function rejectText(reason: string | null | undefined, t: T): string {
  if (reason && /^Schadsoftware/i.test(reason)) return t('studioUpload.virus');
  return reason ? t('studioUpload.rejectedReason', { reason }) : t('studioUpload.rejected');
}

/**
 * Uploads aus dem Studio, auf Ebene der Seite gehalten: Die Prüfung läuft weiter, auch wenn ein anderer Baustein
 * gewählt wird. `onReady` bekommt die neue sha256 (die Verarbeitung erzeugt eine neue Datei, daher erst nach „bereit").
 */
export function useStudioUploads(media: Media[], onReady: (row: StudioUploadRow, sha256: string) => void) {
  const qc = useQueryClient();
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState<StudioUploadRow[]>([]);
  const ready = useRef(onReady);
  ready.current = onReady;
  const upd = (key: number, p: Partial<StudioUploadRow>) => setRows((r) => r.map((x) => (x.key === key ? { ...x, ...p } : x)));

  const start = useCallback(async (elId: string, field: 'asset' | 'assets', kind: UploadKind, files: File[]) => {
    const list = field === 'assets' ? files : files.slice(0, 1);
    for (const f of list) {
      const key = ++seq;
      const row: StudioUploadRow = { key, elId, field, kind, name: f.name, sent: 0, total: f.size, phase: 'upload' };
      if (!fitsKind(f, kind)) {
        setRows((r) => [...r, { ...row, phase: 'error', error: t(`studioUpload.wrongType.${kind}`) }]);
        continue;
      }
      setRows((r) => [...r, row]);
      try {
        const res = await uploadFile(f, (sent, total) => upd(key, { sent, total }));
        upd(key, { phase: 'check', assetId: res.assetId, sent: f.size });
      } catch (e) {
        upd(key, { phase: 'error', error: uploadErrorText(e, t, localeOf(i18n.language)) });
      }
    }
    await Promise.all([qc.invalidateQueries({ queryKey: qk.media }), qc.invalidateQueries({ queryKey: qk.usage })]);
  }, [qc, t, i18n.language]);

  // Prüfung abwarten: useMedia fragt nach, solange etwas in Prüfung ist
  useEffect(() => {
    const done: number[] = [];
    for (const r of rows) {
      if (r.phase !== 'check' || !r.assetId) continue;
      const v = media.find((m) => m.id === r.assetId)?.current;
      if (v?.status === 'ready') {
        done.push(r.key);
        ready.current(r, v.sha256);
      } else if (v?.status === 'rejected') {
        upd(r.key, { phase: 'error', error: rejectText(v.rejectReason, t) });
      }
    }
    if (!done.length) return;
    setRows((x) => x.map((r) => (done.includes(r.key) ? { ...r, phase: 'done' } : r)));
    // „Ausgewählt" kurz zeigen; nicht beim nächsten Durchlauf abbrechen
    setTimeout(() => setRows((x) => x.filter((r) => !done.includes(r.key))), 3000);
  }, [media, rows, t]);

  const dismiss = useCallback((key: number) => setRows((r) => r.filter((x) => x.key !== key)), []);
  return { rows, start, dismiss };
}
export type StudioUploads = ReturnType<typeof useStudioUploads>;

/** Fortschritt, „In Prüfung …", Fehler je Datei. */
function Rows({ rows, onDismiss }: { rows: StudioUploadRow[]; onDismiss: (key: number) => void }) {
  const { t } = useTranslation();
  if (!rows.length) return null;
  return (
    <div className="s-uploads" aria-live="polite" data-testid="studio-uploads">
      {rows.map((r) => {
        const pct = Math.round(r.total ? (r.sent / r.total) * 100 : 0);
        return (
          <div key={r.key} className={`s-up ${r.phase}`}>
            <span className="n" title={r.name}>{r.name}</span>
            {r.phase === 'upload' && <><div className="bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={r.name}><div style={{ width: `${pct}%` }} /></div><span className="small muted">{pct} %</span></>}
            {r.phase === 'check' && <span className="small muted s-check"><span className="s-spin" aria-hidden />{t('studioUpload.checking')}</span>}
            {r.phase === 'done' && <span className="small s-ok">{t('studioUpload.selected')}</span>}
            {r.phase === 'error' && <>
              <span className="field-error" role="alert">{r.error}</span>
              <button type="button" className="btn sm ghost icon" aria-label={t('common.close')} onClick={() => onDismiss(r.key)}><Icon name="x" /></button>
            </>}
          </div>
        );
      })}
    </div>
  );
}

/** Hülle eines Medienfelds: Knopf „Hochladen", Drag-and-drop aufs Feld, Fortschritt. Ohne Recht nur der Inhalt. */
export function UploadArea({ elId, field, kind, uploads, canUpload, children, empty }: {
  elId: string; field: 'asset' | 'assets'; kind: UploadKind; uploads: StudioUploads | undefined; canUpload: boolean; children: ReactNode; empty: boolean;
}) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const on = canUpload && !!uploads;
  const rows = uploads?.rows.filter((r) => r.elId === elId) ?? [];
  const drop = on ? {
    onDragOver: (e: DragEvent) => { if ([...e.dataTransfer.types].includes('Files')) { e.preventDefault(); setOver(true); } },
    onDragLeave: () => setOver(false),
    onDrop: (e: DragEvent) => { e.preventDefault(); setOver(false); void uploads.start(elId, field, kind, [...e.dataTransfer.files]); },
  } : {};
  return (
    <div className={`s-media${over ? ' over' : ''}`} data-testid="studio-media-field" {...drop}>
      {children}
      {empty && (
        <div className="s-empty">
          <Icon name={kind === 'video' ? 'video' : 'image'} />
          <span>{on ? t(`studioUpload.empty.${kind}`) : t(`studioUpload.emptyNoRight.${kind}`)}</span>
        </div>
      )}
      {on && <>
        <div className="row" style={{ gap: 8 }}>
          <button type="button" className={`btn sm${empty ? ' primary' : ''}`} onClick={() => input.current?.click()}><Icon name="upload" />{t('studioUpload.button')}</button>
          <span className="small muted">{t('studioUpload.dropHint')}</span>
        </div>
        <input ref={input} type="file" hidden data-testid="studio-file-input" accept={ACCEPT[kind].join(',')} multiple={field === 'assets'}
          onChange={(e) => { void uploads.start(elId, field, kind, [...(e.target.files ?? [])]); e.target.value = ''; }} />
      </>}
      <Rows rows={rows} onDismiss={(k) => uploads?.dismiss(k)} />
    </div>
  );
}

/** Auswahlfeld „Bild"/„Video" mit Vorschaubild des gewählten Mediums. */
export function AssetSelect({ label, kind, value, media, disabled, onChange }: {
  label: string; kind: UploadKind; value: string | undefined; media: Media[]; disabled: boolean; onChange: (sha: string | undefined) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const list = media.filter((m) => m.kind === kind && m.current?.status === 'ready');
  const cur = value ? list.find((m) => m.current!.sha256 === value) : undefined;
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div className="s-pick">
        <div className="s-thumb" data-testid="studio-asset-thumb">{cur ? <MediaView media={cur} /> : <Icon name={kind === 'video' ? 'video' : 'image'} />}</div>
        <select id={id} disabled={disabled} value={value ?? ''} onChange={(e) => onChange(e.target.value || undefined)}>
          <option value="">{t('design.choose')}</option>
          {list.map((m) => <option key={m.id} value={m.current!.sha256}>{m.name}</option>)}
        </select>
      </div>
    </div>
  );
}
